"use client";

import { c as _c } from "react/compiler-runtime";
import { type ReactElement, useEffect, useState } from "react";
import OpenGraphPreview, { hasOpenGraphContent, LinkPreviewCardLayout, type OpenGraphPreviewData } from "./OpenGraphPreview";
import { fetchLinkPreview } from "@/services/api/link-preview-api";
import EnsPreviewCard from "./ens/EnsPreviewCard";
import { isEnsPreview, type EnsPreview } from "./ens/types";
interface LinkPreviewCardProps {
  readonly href: string;
  readonly renderFallback: () => ReactElement;
}
type PreviewState = {
  readonly type: "loading";
} | {
  readonly type: "fallback";
} | {
  readonly type: "success";
  readonly data: OpenGraphPreviewData;
} | {
  readonly type: "ens";
  readonly data: EnsPreview;
};
const toPreviewData = (response: Awaited<ReturnType<typeof fetchLinkPreview>>): OpenGraphPreviewData => {
  if (!response) {
    return {};
  }
  return {
    ...response,
    image: response.image ?? undefined,
    images: response.images ?? undefined,
    url: response.url ?? response.requestUrl ?? undefined,
    siteName: response.siteName ?? undefined,
    description: response.description ?? undefined,
    title: response.title ?? undefined
  };
};
export default function LinkPreviewCard(t0) {
  const $ = _c(21);
  const {
    href,
    renderFallback
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      type: "loading"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [state, setState] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== href) {
    t2 = () => {
      let active = true;
      setState({
        type: "loading"
      });
      fetchLinkPreview(href).then(response => {
        if (!active) {
          return;
        }
        if (isEnsPreview(response)) {
          setState({
            type: "ens",
            data: response
          });
          return;
        }
        const previewData = toPreviewData(response);
        if (hasOpenGraphContent(previewData)) {
          setState({
            type: "success",
            data: previewData
          });
          return;
        }
        setState({
          type: "fallback"
        });
      }).catch(() => {
        if (active) {
          setState({
            type: "fallback"
          });
        }
      });
      return () => {
        active = false;
      };
    };
    t3 = [href];
    $[1] = href;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  if (state.type === "fallback") {
    let t4;
    if ($[4] !== renderFallback) {
      t4 = renderFallback();
      $[4] = renderFallback;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    const fallbackContent = t4;
    let t5;
    if ($[6] !== fallbackContent) {
      t5 = <div className="tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4"><div className="tw-flex tw-h-full tw-w-full tw-max-w-full tw-items-center tw-justify-start tw-overflow-hidden tw-break-words tw-[overflow-wrap:anywhere]">{fallbackContent}</div></div>;
      $[6] = fallbackContent;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== href || $[9] !== t5) {
      t6 = <LinkPreviewCardLayout href={href}>{t5}</LinkPreviewCardLayout>;
      $[8] = href;
      $[9] = t5;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    return t6;
  }
  if (state.type === "success") {
    let t4;
    if ($[11] !== href || $[12] !== state.data) {
      t4 = <OpenGraphPreview href={href} preview={state.data} />;
      $[11] = href;
      $[12] = state.data;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    return t4;
  }
  if (state.type === "ens") {
    let t4;
    if ($[14] !== state.data) {
      t4 = <div className="tw-w-full tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4"><EnsPreviewCard preview={state.data} /></div>;
      $[14] = state.data;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] !== href || $[17] !== t4) {
      t5 = <LinkPreviewCardLayout href={href}>{t4}</LinkPreviewCardLayout>;
      $[16] = href;
      $[17] = t4;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    return t5;
  }
  let t4;
  if ($[19] !== href) {
    t4 = <OpenGraphPreview href={href} preview={undefined} />;
    $[19] = href;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  return t4;
}